import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MainNav from '@/components/layout/AppNavbar';
import IdolCard from '@/components/ui/IdolCard';
import WorldCard from '@/components/ui/WorldCard';
import Pagination from '@/components/ui/Pagination';
import Tabs from '@/components/ui/Tabs';
import Loading from '@/components/ui/Loading';
import StatusModal from '@/components/ui/StatusModal';
import Axios from '@/shared/api/axios';

const LIMIT = 12;

// 與偶像頁「關注的偶像／關注的世界觀」tab 同一組 API，view_idol_id 帶自己即為我的關注
const TAB_ENDPOINTS = {
    idol: '/idol/get-follow-idol-list',
    world: '/idol/get-link-world-list',
};

/**
 * 我的關注頁面：目前偶像關注的偶像、世界觀
 * tab、page 記在 URL（?tab=world&page=2），從卡片點進去再返回時回到原本的 tab 與頁數
 */
const FollowingPage = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const idolId = useSelector(state => state.user.idolId);

    const activeTab = searchParams.get('tab') === 'world' ? 'world' : 'idol';
    const currentPage = parseInt(searchParams.get('page') || '1', 10);

    // 記錄資料屬於哪個偶像、哪個 tab：切換 tab 期間不會用世界觀卡片渲染偶像資料（反之亦然），
    // 切換目前偶像後若新請求失敗，也不會留著上一個偶像的關注列表
    const [list, setList] = useState(null); // { idolId, tab, items, total }
    const [isLoading, setIsLoading] = useState(false);
    const [currentStatusCode, setCurrentStatusCode] = useState('');
    const statusModalRef = useRef(null);

    useEffect(() => {
        if (!idolId) return;

        // 快速切換 tab／頁數時，較晚回來的舊 request 不可蓋掉新的資料
        let ignore = false;

        const fetchList = async () => {
            setIsLoading(true);
            try {
                const res = await Axios.post(TAB_ENDPOINTS[activeTab], {
                    idol_id: idolId,
                    view_idol_id: idolId,
                    page: currentPage,
                    limit: LIMIT,
                });
                if (ignore) return;
                if (res.data.status_code === 'SYSTEM_1000') {
                    setList({ idolId, tab: activeTab, items: res.data.data, total: res.data.total });
                } else {
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                }
            } catch (error) {
                if (ignore) return;
                // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
                if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                    setCurrentStatusCode(error.httpErrorCode);
                    statusModalRef.current?.show();
                } else if (!error.httpErrorCode) {
                    console.error('Failed to fetch following list:', error);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        fetchList();
        return () => { ignore = true; };
    }, [idolId, activeTab, currentPage]);

    // 切換 tab 回到第一頁
    const handleTabChange = (tab) => {
        setSearchParams({ tab });
    };

    const handlePageChange = (page) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', page);
        // 換頁捲動交給 Pagination 的 scrollMode="window" 處理，避免跟 ScrollRestoration 的瞬間置頂互搶
        setSearchParams(newParams, { preventScrollReset: true });
    };

    const items = list?.idolId === idolId && list.tab === activeTab ? list.items : null;

    return (
        <>
            <MainNav />
            <Container fluid="lg" className="nav-padding">
                <Row className="pt-4">
                    <Col xs={12}>
                        <h3 className="mb-4">{t('my_following')}</h3>

                        <Tabs
                            tabs={[
                                { key: 'idol', label: t('idol') },
                                { key: 'world', label: t('world') }
                            ]}
                            activeTab={activeTab}
                            onChange={handleTabChange}
                            className="mb-4"
                        />

                        {items && (items.length > 0 ? (
                            <div className="cards-grid mb-4 mt-5">
                                {activeTab === 'idol'
                                    ? items.map((item) => (
                                        <IdolCard
                                            key={item.idol_id}
                                            idol={{
                                                // 沿用 IdolCard 的 subscribed_* 命名，將 API 的 idol_* 映射進去
                                                subscribed_idol_id: item.idol_id,
                                                subscribed_idol_uuid: item.idol_uuid,
                                                subscribed_idol_name: item.idol_nick_name,
                                                subscribed_idol_is_official: item.idol_is_official,
                                                subscribed_idol_avatar_url: item.idol_big_avatar_url,
                                                subscribed_idol_cover_url: item.idol_cover_url
                                            }}
                                            suspended={item.idol_is_suspended}
                                            showOnline
                                        />
                                    ))
                                    : items.map((item) => (
                                        <WorldCard
                                            key={item.world_id}
                                            // API 的封面欄位是 world_media_url，WorldCard 讀 world_cover_url
                                            world={{ ...item, world_cover_url: item.world_media_url }}
                                        />
                                    ))}
                            </div>
                        ) : (
                            <div className="text-center py-5 text-muted mb-4">
                                {activeTab === 'idol' ? t('no_followed_idol') : t('no_followed_world')}
                            </div>
                        ))}

                        {items && list.total > LIMIT && (
                            <div className="d-flex justify-content-center">
                                <Pagination
                                    total={list.total}
                                    limit={LIMIT}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    scrollMode="window"
                                />
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>

            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
            {isLoading && <Loading />}
        </>
    );
};

export default FollowingPage;
